import { type CustomFieldType, type FieldConfig, isBlockField } from '@cod/config-schema';

/**
 * What each element in the fields list is CALLED in the designer.
 *
 * 🚨 A custom field's stored `label` is never its NAME, on any type — the card is titled from the
 * TYPE, in the dashboard's language. The label is two different things and neither is a name:
 *
 *  - On the **input** types (`text`, `dropdown`, `single_choice`, `checkbox`, `date`) it is the
 *    buyer-facing label printed above the input. It belongs to the STORE's language, not the
 *    dashboard's, and the merchant edits it inside the card — so a card titled from it renamed
 *    itself as they typed, and a Spanish storefront run from an English dashboard listed its
 *    fields in Spanish. "Dropdown list" says what the row IS; "Talla" is its content.
 *  - On the **block** types (`custom_text`, `custom_image`, `whatsapp_button`, `link_button`) no
 *    label is ever rendered to a buyer — `blocks.ts` builds those from `text_block` / `image` /
 *    `whatsapp` / `link` — and there is no editor for it either. It is pure designer chrome.
 *    `customFieldDefaults` seeds that chrome in Spanish, so a block field added from an English or
 *    Portuguese dashboard came out titled "Texto personalizado" / "Comprar por WhatsApp" and
 *    stayed that way: unreadable to the merchant, with no control to fix it, and unchanged when
 *    they switched language because it is stored data rather than a translation.
 *
 * The strings already exist — they are the ones the "Add element" modal lists, and sharing this
 * one map is what keeps the name a merchant picks from the modal the name they then see on the
 * card they just added.
 */
const I18N_SUFFIX: Record<CustomFieldType, string> = {
  custom_text: 'customText',
  custom_image: 'image',
  whatsapp_button: 'whatsapp',
  text: 'textInput',
  dropdown: 'dropdown',
  single_choice: 'singleChoice',
  checkbox: 'checkbox',
  date: 'date',
  link_button: 'link',
};

type Translate = (key: string, opts?: Record<string, unknown>) => string;

/** The `designer.addField.*` leaf a custom type is named by, or null for a type we do not name. */
export function customFieldTypeI18nSuffix(type: FieldConfig['type']): string | null {
  return I18N_SUFFIX[type as CustomFieldType] ?? null;
}

/** The merchant-facing name of a custom field type, or null for a type we do not name. */
export function customFieldTypeName(type: FieldConfig['type'], t: Translate): string | null {
  const suffix = customFieldTypeI18nSuffix(type);
  return suffix ? t(`designer.addField.${suffix}`) : null;
}

/** The one-line description under a type's name in the "Add element" modal. */
export function customFieldTypeDescription(type: CustomFieldType, t: Translate): string {
  return t(`designer.addField.${I18N_SUFFIX[type]}Desc`);
}

/**
 * The built-in fields whose card carries a FIXED name, keyed by field key.
 *
 * 🚨 Keyed by `key`, not by `label`. The card used to be built from the merchant's own label —
 * "Campo: {label}" / "{label} field" — so renaming a label renamed the card, and a merchant who
 * relabelled "Provincia" to "Departamento" lost the only thing telling them which field they were
 * looking at. The key is the one part of a built-in field they cannot change, which is exactly what
 * a name should be built from. The label is still edited inside the card, and still what buyers
 * read on the storefront.
 *
 * `newsletter` and `terms` are in here despite being block types: they name a field the buyer
 * fills in, so they read better with the rest of this list than as sections next to
 * "Totals summary". Every other built-in block keeps `designer.blocks.*`.
 */
const NAMED_FIELD_KEYS = new Set([
  'first_name',
  'last_name',
  'phone',
  'address',
  'address_2',
  'province',
  'city',
  'postal_code',
  'email',
  'order_note',
  'newsletter',
  'terms',
]);

/**
 * The title on a field's card in the designer list.
 *
 * In order:
 *   named built-in → its fixed name (`NAMED_FIELD_KEYS`)
 *   nameable type  → the TYPE's name — "Dropdown list", "Date selector", …
 *   built-in block → the fixed section name
 *   anything else  → "{label} field", the last resort
 *
 * 🚨 **The type branch is keyed on the TYPE, never on the `custom` flag.** It used to test
 * `field.custom === true` first, and `custom` is `z.boolean().default(false)` — so every custom
 * field stored before the flag existed (`d9c3f9a` is where `customFieldDefaults` started setting
 * it) reads back as `false`, missed the branch, and fell all the way through to "Campo: {label}".
 * Those merchants watched their card rename itself as they typed a label, which is the exact
 * behaviour `NAMED_FIELD_KEYS` exists to prevent. A `dropdown` field IS a "Dropdown list"
 * regardless of how it was created; provenance is not a naming input.
 *
 * 🚨 **`NAMED_FIELD_KEYS` has to stay ahead of it.** `first_name` is type `text`, so the type
 * branch would otherwise title it "Text input" and undo the fixed built-in names.
 * The two sets cannot collide in practice — custom keys are `cf_<uuid>` — but the ORDER is what
 * guarantees it rather than the key format.
 */
export function fieldCardTitle(field: FieldConfig, t: Translate): string {
  if (NAMED_FIELD_KEYS.has(field.key)) return t(`designer.fieldNames.${field.key}`);
  const byType = customFieldTypeName(field.type, t);
  if (byType) return byType;
  return isBlockField(field.type)
    ? t(`designer.blocks.${field.type}`)
    : t('designer.fields.fieldTitle', { label: field.label });
}
